import React from 'react';
import { useTranslation } from 'react-i18next';
import { faSunrise } from '@fortawesome/pro-solid-svg-icons/faSunrise';
import { faSunBright } from '@fortawesome/pro-solid-svg-icons/faSunBright';
import { faSunset } from '@fortawesome/pro-solid-svg-icons/faSunset';
import { faMoonStars } from '@fortawesome/pro-solid-svg-icons/faMoonStars';
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan';

import '../i18n/config';
import { GeneralTime } from '../constants';
import { GeneralAvailabilityDay } from '../types';
import { Week } from './Week';

interface Props {
  availability: GeneralAvailabilityDay[];
  setAvailability(availability: GeneralAvailabilityDay[]): void;
}

export const GeneralAvailability = ({ availability, setAvailability }: Props): JSX.Element => {
  const { t } = useTranslation();

  const deselectTime = (time: GeneralTime): void => {
    const modified = [...availability];

    modified.forEach(day => {
      day[time] = false;
    });

    setAvailability(modified);
  };

  const isDaySelected = (index: number, time: GeneralTime): boolean => availability[index][time];

  const isTimeNeverSelected = (time: GeneralTime): boolean => availability.filter(x => x[time] === true).length === 0;

  const toggleDay = (index: number, time: GeneralTime): void => {
    const modified = [...availability];

    if (time === GeneralTime.never) {
      modified[index].timeEarly = false;
      modified[index].timeMorning = false;
      modified[index].timeAfternoon = false;
      modified[index].timeEvening = false;
      modified[index].timeOvernight = false;
      modified[index].timeNever = true;
    } else {
      modified[index][time] = !modified[index][time];
      modified[index].timeNever = false;
    }

    setAvailability(modified);
  };

  return (
    <div>
      <Week
        availability={availability}
        deselectTime={deselectTime}
        isDaySelected={isDaySelected}
        isTimeNeverSelected={isTimeNeverSelected}
        toggleDay={toggleDay}
        time={GeneralTime.early}
        icon={faSunrise}
        iconColor='text-tansy'
        backgroundColor='bg-tansy-300'
        title={t('components.generalAvailability.timeEarly')}
        never
      />
      <Week
        availability={availability}
        deselectTime={deselectTime}
        isDaySelected={isDaySelected}
        isTimeNeverSelected={isTimeNeverSelected}
        toggleDay={toggleDay}
        time={GeneralTime.morning}
        icon={faSunBright}
        iconColor='text-honey'
        backgroundColor='bg-honey-300'
        title={t('components.generalAvailability.timeMorning')}
        never
      />
      <Week
        availability={availability}
        deselectTime={deselectTime}
        isDaySelected={isDaySelected}
        isTimeNeverSelected={isTimeNeverSelected}
        toggleDay={toggleDay}
        time={GeneralTime.afternoon}
        icon={faSunBright}
        iconColor='text-honey'
        backgroundColor='bg-honey-300'
        title={t('components.generalAvailability.timeAfternoon')}
        never
      />
      <Week
        availability={availability}
        deselectTime={deselectTime}
        isDaySelected={isDaySelected}
        isTimeNeverSelected={isTimeNeverSelected}
        toggleDay={toggleDay}
        time={GeneralTime.evening}
        icon={faSunset}
        iconColor='text-bubblegum'
        backgroundColor='bg-bubblegum-300'
        title={t('components.generalAvailability.timeEvening')}
        never
      />
      <Week
        availability={availability}
        deselectTime={deselectTime}
        isDaySelected={isDaySelected}
        isTimeNeverSelected={isTimeNeverSelected}
        toggleDay={toggleDay}
        time={GeneralTime.overnight}
        icon={faMoonStars}
        iconColor='text-sapphire-700'
        backgroundColor='bg-sapphire-300'
        title={t('components.generalAvailability.timeOvernight')}
        never
      />
      <Week
        availability={availability}
        deselectTime={deselectTime}
        isDaySelected={isDaySelected}
        isTimeNeverSelected={isTimeNeverSelected}
        toggleDay={toggleDay}
        time={GeneralTime.never}
        icon={faBan}
        iconColor='text-neutral-900'
        backgroundColor='bg-emerald-300'
        title={t('components.generalAvailability.timeNever')}
      />
    </div>
  );
};
